/**
 * Long + short labels for every canonical field. Matches the keys in
 * `frontend/constants/fields.ts`.
 *
 * Lives in `shared/` because the generated PDFs render on the server and would
 * otherwise restate these labels — the two drift the moment either side is
 * edited alone. The frontend reaches them through `useField(key)`.
 */
// `short` is only present where a surface actually renders a compact form —
// a table column, a chip. `useField` falls back to `label` where none exists.
import type { FieldKey } from '../../constants/field-key'

import { COMMON } from './common'
import { TERMS } from './terms'

type FieldLabel = { label: string; short?: string }

export const FIELD_LABELS = {
  // Areas
  livingArea: { label: 'Wohnfläche', short: 'Wohnfl.' },
  usableArea: { label: 'Nutzfläche' },
  landArea: { label: 'Grundstück', short: 'Grundst.' },
  gardenArea: { label: 'Gartenfläche' },
  balconyArea: { label: 'Balkonfläche' },
  wallArea: { label: 'Wandfläche' },

  // Counts
  numberOfRooms: { label: 'Zimmer' },
  numberOfBathrooms: { label: 'Badezimmer', short: 'Bäder' },
  numberOfFloors: { label: `${TERMS.fullFloor}e` },
  numberOfIndoorParkingSpaces: { label: 'Innenstellplätze' },
  numberOfOutdoorParkingSpaces: {
    label: 'Außenstellplätze',
    short: 'Stellpl.',
  },
  floorNumber: { label: 'Etage' },

  // Timestamps
  createdAt: { label: 'Erstellt am', short: 'Erstellt' },
  updatedAt: { label: 'Zuletzt geändert', short: 'Geändert' },
  lastSeenAt: { label: 'Zuletzt gesehen', short: 'Gesehen' },

  // Years
  constructionYear: { label: 'Baujahr' },
  renovationYear: { label: 'Zuletzt renoviert' },

  // Energy
  energyLabel: { label: 'Energieeffizienzklasse' },
  energyConsumption: { label: 'Energieverbrauch' },

  // Prices
  marketPrice: { label: 'Kaufpreis', short: 'Preis' },
  potentialPrice: { label: 'Wert nach Sanierung' },

  // Flags
  leasehold: { label: 'Erbbaurecht' },
  isNew: { label: 'Neubau' },

  // Property type
  type: { label: 'Objektart', short: 'Art' },
  subType: { label: 'Objektunterart', short: 'Unterart' },

  // Generic entity fields
  email: { label: TERMS.email },
  title: { label: 'Titel' },
  name: { label: 'Name' },
  /** A person's family name, or an organisation's name — one column, so one
   *  label that fits both. */
  lastName: { label: 'Name' },
  firstName: { label: 'Vorname' },
  salutation: { label: 'Anrede' },
  academicTitle: { label: 'Titel' },
  customerNumber: { label: 'Kundennummer', short: 'Kd.-Nr.' },
  userType: { label: 'Art' },
  phoneType: { label: 'Art' },
  note: { label: 'Hinweis' },
  roles: { label: 'Rollen' },
  status: { label: 'Status' },
  configuratorStatus: { label: TERMS.configurator },
  notes: { label: 'Notizen' },
  description: { label: 'Beschreibung' },
  /** What the offer states a measure or a position as. */
  serviceDescription: { label: TERMS.serviceDescription, short: 'LV-Text' },
  /** What the advisor tells one customer about one position. Says the same
   *  word as `note`, and is a different field on a different entity. */
  customerHint: { label: 'Hinweis' },
  icon: { label: 'Icon' },

  // Address
  address: { label: 'Adresse' },
  street: { label: 'Straße' },
  houseNumber: { label: 'Hausnummer' },
  postalCode: { label: 'PLZ' },
  city: { label: 'Stadt' },
  district: { label: 'Stadtteil' },

  // People
  contactPerson: { label: TERMS.advisor },
  projectManager: { label: TERMS.projectManager },
  siteManager: { label: TERMS.siteManager },
  customer: { label: TERMS.customer },
  phone: { label: 'Telefon' },
  gender: { label: 'Anrede' },
  jobTitle: { label: 'Berufsbezeichnung' },
  calendarBookingUrl: { label: 'Kalender' },

  // Rooms
  roomType: { label: 'Raumtyp' },
  productStandard: { label: TERMS.productStandard, short: 'Standard' },
  productCategory: { label: TERMS.productCategory },
  productGroup: { label: TERMS.productGroup, short: 'Gruppe' },
  designLine: { label: TERMS.designLine },
  collection: { label: 'Kollektion' },
  isActive: { label: 'Aktiv' },
  floor: { label: TERMS.floor },
  length: { label: 'Länge' },
  width: { label: 'Breite' },
  height: { label: 'Höhe' },
  perimeter: { label: 'Umlauf' },
  area: { label: 'Fläche' },

  // Cost entries
  flatRateCost: { label: 'Preis' },
  quantity: { label: 'Anzahl' },
  /** How often a price row takes what it charges — an area, a count, a
   *  number of hours, so not the cost entry's whole `Anzahl`. */
  rowCount: { label: 'Menge' },
  unit: { label: 'Einheit' },

  // Finances
  netAmount: { label: 'Nettobetrag' },
  grossAmount: { label: 'Bruttobetrag' },
  vatRate: { label: 'USt.-Satz', short: 'USt.' },
  percent: { label: 'Anteil' },
  amount: { label: COMMON.amount },
  invoiceNumber: { label: 'Rechnungsnummer' },
  invoiceDate: { label: 'Rechnungsdatum' },
  dueDate: { label: 'Fällig am' },
  paidAt: { label: 'Bezahlt am' },
  approvedAt: { label: 'Freigegeben am' },
  signedAt: { label: 'Vertragsdatum' },
  reachedAt: { label: 'Erreicht am' },
  paymentTermDays: { label: 'Zahlungsziel' },
  iban: { label: 'IBAN' },
  reference: { label: 'Verwendungszweck' },
  contactName: { label: TERMS.contactPerson },
} as const satisfies Record<FieldKey, FieldLabel>
